/**
 * BookDetailModal
 * ===============
 * Full-screen modal showing book details and the borrow request form.
 * Opens when user clicks a BookCard anywhere in the app.
 */

import React, { useState } from "react";
import { colors, fonts }   from "@/theme/tokens";
import { useBook }         from "@/hooks/useBooks";
import { useAuth }         from "@/hooks/useAuth";
import { useUIStore }      from "@/store/uiStore";
import { CategoryBadge, DistanceBadge } from "@/components/ui/Badge";
import { Button }          from "@/components/ui/Button";

const REMINDER_OPTIONS = [
  { id:"email",    label:"✉ Email"     },
  { id:"sms",      label:"💬 SMS"      },
  { id:"push",     label:"📱 App push" },
  { id:"whatsapp", label:"🟢 WhatsApp" },
] as const;

export const BookDetailModal: React.FC = () => {
  const { hideModal, modalData, showModal } = useUIStore();
  const { isSignedIn, user } = useAuth();
  const bookId = modalData["bookId"] as string | undefined;

  const { data: book, isLoading } = useBook(bookId ?? "");

  const [weeks,     setWeeks]     = useState(2);
  const [delivery,  setDelivery]  = useState<"meetup" | "deliver">("meetup");
  const [reminders, setReminders] = useState<string[]>(["email"]);
  const [requested, setRequested] = useState(false);

  if (!bookId) return null;

  const handleRequest = async () => {
    if (!isSignedIn || !user) { showModal("signIn"); return; }
    if (!book) return;
    const { createRequest } = await import("@/features/requests/requestsService");
    const result = await createRequest({
      bookId:                 book.id,
      bookTitle:              book.title,
      bookCoverUrl:           book.coverUrl,
      lenderId:               book.ownerId,
      borrowerId:             user.uid,
      borrowerName:           user.displayName ?? "Anonymous",
      requestedDurationWeeks: weeks,
      deliveryType:           delivery,
      reminderMethods:        reminders as import("@/types").ReminderMethod[],
    });
    if (result.ok) setRequested(true);
  };

  const due = new Date();
  due.setDate(due.getDate() + weeks * 7);
  const dueStr = due.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });

  const toggleReminder = (id: string) =>
    setReminders(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);

  return (
    <div
      onClick={e => e.target === e.currentTarget && hideModal()}
      style={{
        position:"fixed", inset:0, zIndex:1000,
        background:"rgba(15,10,40,.72)",
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:20, animation:"adhvi-fadeIn .18s ease",
      }}
    >
      <div style={{
        background:"#fff", borderRadius:22, overflow:"hidden",
        width:"100%", maxWidth:440, border:`1px solid ${colors.border}`,
        animation:"adhvi-slideUp .22s ease", maxHeight:"92vh", overflowY:"auto",
      }}>
        {isLoading ? (
          <div style={{ height:300, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ width:36, height:36, border:`3px solid ${colors.border}`, borderTopColor:colors.primary, borderRadius:"50%", animation:"adhvi-spin .7s linear infinite" }}/>
          </div>
        ) : !book ? (
          <div style={{ padding:32, textAlign:"center", color: colors.textSub }}>Book not found.</div>
        ) : (
          <>
            {/* Cover */}
            <div style={{ position:"relative", height:160, overflow:"hidden", background: colors.surfaceAlt }}>
              {book.coverUrl
                ? <img src={book.coverUrl} alt={`Cover of ${book.title}`} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                : <div style={{ height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:56 }}>📚</div>
              }
              <button onClick={hideModal} style={{ position:"absolute", top:12, right:12, width:30, height:30, background:"rgba(255,255,255,.85)", border:"none", borderRadius:8, cursor:"pointer", fontSize:15 }}>✕</button>
              <div style={{ position:"absolute", bottom:10, left:14, display:"flex", gap:6 }}>
                <DistanceBadge distance={book.distanceMiles ?? 0} isNew={book.isNew}/>
                <CategoryBadge category={book.category}/>
              </div>
            </div>

            {/* Media strip if multiple */}
            {book.media.length > 1 && (
              <div style={{ display:"flex", gap:6, padding:"8px 12px", background: colors.surfaceAlt, borderBottom:`1px solid ${colors.border}`, overflowX:"auto" }}>
                {book.media.map((m, i) => (
                  <div key={i} style={{ width:44, height:44, borderRadius:8, overflow:"hidden", flexShrink:0, border:`2px solid ${i === 0 ? colors.primary : "transparent"}`, cursor:"pointer" }}>
                    {m.type === "video"
                      ? <div style={{ width:"100%", height:"100%", background:"#000", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:"#fff" }}>▶</div>
                      : <img src={m.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                    }
                  </div>
                ))}
              </div>
            )}

            <div style={{ padding:"18px 20px" }}>
              {/* Title + meta */}
              <h2 style={{ fontSize:19, fontFamily: fonts.display, fontWeight:800, color: colors.text, letterSpacing:"-0.4px", marginBottom:4 }}>
                {book.title}
              </h2>
              <p style={{ fontSize:12, color: colors.textSub, marginBottom:10 }}>by {book.author}</p>

              {/* Rating */}
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>
                <span style={{ color:"#F59E0B", fontSize:14 }}>{"★".repeat(Math.round(book.rating))}</span>
                <span style={{ fontSize:12, fontWeight:700, color:"#92400E" }}>{book.rating.toFixed(1)}</span>
                <span style={{ fontSize:11, color: colors.textMuted }}>({book.reviewCount} reviews)</span>
              </div>

              {/* Description */}
              {book.description && (
                <p style={{ fontSize:13, color:"#5F5E5A", lineHeight:1.65, marginBottom:16 }}>
                  {book.description}
                </p>
              )}

              {/* Owner */}
              <div style={{ background: colors.surfaceAlt, borderRadius:11, padding:"10px 13px", display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background: colors.primary, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"#fff", flexShrink:0 }}>
                  {book.ownerInitials}
                </div>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color: colors.dark }}>Shared by {book.ownerName}</div>
                  <div style={{ fontSize:10, color: colors.textSub, fontWeight:500 }}>Community member · {book.location.area}</div>
                </div>
              </div>

              {requested ? (
                <div style={{ background: colors.success50, border:`1px solid #6EE7B7`, borderRadius:12, padding:"14px", textAlign:"center" }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>🎉</div>
                  <div style={{ fontSize:14, fontWeight:800, color: colors.accent }}>Request sent!</div>
                  <div style={{ fontSize:11, color:"#065F46", marginTop:4 }}>The owner will get back to you soon.</div>
                </div>
              ) : (
                <>
                  {/* Duration picker */}
                  <div style={{ background: colors.surface, border:`1px solid ${colors.border}`, borderRadius:12, padding:"13px 14px", marginBottom:12 }}>
                    <div style={{ fontSize:12, fontWeight:700, color: colors.text, marginBottom:10 }}>⏱ How long do you need it?</div>
                    <input
                      type="range" min={1} max={book.maxDurationWeeks} value={weeks} step={1}
                      onChange={e => setWeeks(Number(e.target.value))}
                      style={{ width:"100%", accentColor: colors.primary, marginBottom:6 }}
                    />
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:11, color: colors.textSub }}>Due: <strong style={{ color: colors.primary }}>{dueStr}</strong></span>
                      <span style={{ fontSize:12, fontWeight:700, color: colors.primary }}>{weeks} week{weeks !== 1 ? "s" : ""}</span>
                    </div>
                  </div>

                  {/* Delivery */}
                  <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                    {(["meetup", "deliver"] as const).map(d => (
                      <button key={d} onClick={() => setDelivery(d)} style={{
                        flex:1, padding:"9px 10px",
                        border:`1.5px solid ${delivery === d ? colors.primary : colors.border}`,
                        borderRadius:10, background: delivery === d ? colors.surfaceAlt : "#fff",
                        fontSize:12, fontWeight:700,
                        color: delivery === d ? colors.primary : colors.textSub,
                        cursor:"pointer",
                      }}>
                        {d === "meetup" ? "📍 Meet nearby" : "🚚 Home delivery"}
                      </button>
                    ))}
                  </div>

                  {/* Reminders */}
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:"#5F5E5A", marginBottom:7 }}>🔔 Remind me via</div>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {REMINDER_OPTIONS.map(r => {
                        const on = reminders.includes(r.id);
                        return (
                          <button key={r.id} onClick={() => toggleReminder(r.id)} style={{
                            padding:"5px 11px",
                            border:`1.5px solid ${on ? colors.primary : colors.border}`,
                            borderRadius:20,
                            background: on ? colors.surfaceAlt : "#fff",
                            fontSize:11, fontWeight:700,
                            color: on ? colors.primary : colors.textSub,
                            cursor:"pointer",
                          }}>
                            {r.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Button variant="primary" fullWidth onClick={handleRequest} style={{ marginBottom:8 }}>
                    📬 Request this book
                  </Button>
                  <Button variant="ghost" fullWidth onClick={hideModal}>
                    ← Back to browsing
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
